import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Stack,
  Pagination,
  TextField,
  MenuItem,
  Select,
  Chip
} from '@mui/material';

import { PaginationState } from 'types/custom/common';
import MainCard from 'components/MainCard';
import ScrollX from 'components/ScrollX';
import { EmptyTable } from 'components/third-party/ReactTable';
import { IOrderV2 } from 'types/custom/order';
import { formatDateCommon } from 'utils/formatters';
import { getOrderStatusColor } from 'utils/custom';
// import OrderMenu from './OrderMenu';
import { OrderType } from 'enum/order-type';
// import { useFirebaseContext } from 'contexts/FirebaseContext';
import { TransactionType } from 'enum/transaction-type';
import { Tooltip } from '@mui/material';
import { InfoCircle } from 'iconsax-react';
import { Grid } from '@mui/material';

interface SimulationHistoryProps {
  data: IOrderV2[];
  setPage: (val: PaginationState) => void;
  totalRecords: number;
  page: PaginationState;
  handleRefreshData: () => void;
  setOpenPlaceOrderModal: (val: boolean) => void;
  setOrderType: (type: OrderType | null) => void;
  setOrder: (val: any) => void;
  setFadedRow: (val: number | null) => void;
  loading: boolean;
  handleCancelOrder: () => void;
  setAction: (val: string) => void;
}

const OrdersTableV2: React.FC<SimulationHistoryProps> = React.memo(
  ({
    data,
    setPage,
    totalRecords,
    page,
    handleRefreshData,
    setOpenPlaceOrderModal,
    setOrder,
    setFadedRow,
    setOrderType,
    loading,
    handleCancelOrder,
    setAction
  }) => {
    const [pageSize, setPageSize] = useState(page.limit);
    // const { user } = useFirebaseContext();
    // const permissions = user?.permissions;
    // const hasEditPermission = permissions?.includes('canPlaceManualOrder');
    const handlePageChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
      setPage({ ...page, page: newPage - 1 });
    };

    const handlePageSizeChange = (pageSize: number) => {
      setPage({ ...page, limit: pageSize, page: 0 });
      setPageSize(pageSize);
    };

    const handleDirectPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      let newPage = parseInt(event.target.value, 10);
      if (!isNaN(newPage)) {
        newPage = Math.max(1, Math.min(Math.ceil(totalRecords / page.limit), newPage));
        setPage({ ...page, page: newPage - 1 });
      }
    };

    return (
      <MainCard content={false}>
        <ScrollX>
          <Box>
            <Stack spacing={3}>
              <Box sx={{ p: 3, pb: 0 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                  <Typography variant="h5">Orders:({totalRecords ?? 0})</Typography>
                </Stack>
              </Box>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Order Id</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Broker</TableCell>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>Stock</TableCell>
                    <TableCell>Transaction Type</TableCell>
                    <TableCell>Order Type</TableCell>
                    <TableCell>Strategy</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell>Quantity</TableCell>
                    <TableCell>Status</TableCell>
                    {/* <TableCell>Actions</TableCell> */}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={12} align="center">
                        <Grid sx={{ height: '550px' }} display="flex" alignItems="center" justifyContent="center">
                          Loading...
                        </Grid>
                      </TableCell>
                    </TableRow>
                  ) : data && data?.length > 0 ? (
                    data.map((order, i) => (
                      <TableRow key={i}>
                        <TableCell>{page.page * page.limit + i + 1}</TableCell>
                        <TableCell>{order?.orderId}</TableCell>
                        <TableCell>{order?.firstName + ' ' + order?.lastName}</TableCell>
                        <TableCell>{order?.broker}</TableCell>
                        <TableCell>{formatDateCommon(order?.createdOn, 'dd-MMM-yyyy, hh:mm:ss a')}</TableCell>
                        <TableCell>{order?.stockId}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            variant="combined"
                            label={order?.transactionType}
                            color={order?.transactionType === TransactionType.BUY ? 'primary' : 'error'}
                          />
                        </TableCell>
                        <TableCell>
                          {order?.orderType === OrderType.SL ? 'STOP LOSS' : order?.orderType === OrderType.MARKET ? 'MARKET' : 'REGULAR'}
                        </TableCell>
                        <TableCell>
                          {order?.nonDashBoardOrder ? 'Non Dashboard' : order?.strategy ? order?.strategy : 'Dashboard'}
                        </TableCell>
                        <TableCell>{order?.price.toFixed(2)}</TableCell>
                        <TableCell>{order?.quantity}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Chip
                              variant="light"
                              label={order?.orderStatus?.toUpperCase()}
                              color={getOrderStatusColor(order.orderStatus)}
                            />
                            {(order.orderStatus === 'failed' || order.orderStatus === 'rejected') && order.errorMessage && (
                              <Tooltip title={order.errorMessage} arrow placement="top">
                                <InfoCircle size="20" color="#808080" style={{ cursor: 'pointer', marginLeft: 8 }} />
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                        {/* <TableCell align="left">
                          {hasEditPermission && (
                            <OrderMenu
                              rowIndex={i}
                              setRowIndex={setFadedRow}
                              setOrder={setOrder}
                              setOpenPlaceOrderModal={setOpenPlaceOrderModal}
                              setOrderType={setOrderType}
                              order={order}
                              handleCancelOrder={handleCancelOrder}
                              setAction={setAction}
                            />
                          )}
                        </TableCell> */}
                      </TableRow>
                    ))
                  ) : (
                    <EmptyTable msg="No Orders Found" colSpan={13} />
                  )}
                  <TableRow sx={{ '&:hover': { bgcolor: 'transparent !important' } }}>
                    <TableCell sx={{ p: 2, py: 3 }} colSpan={13}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Typography variant="body2">Row per page</Typography>
                          <Select
                            labelId="page-size-select-label"
                            id="page-size-select"
                            value={pageSize}
                            sx={{ minWidth: '40px', '& .MuiSelect-select': { padding: '4px 8px' } }}
                            onChange={(e) => handlePageSizeChange(e.target.value as number)}
                          >
                            <MenuItem value={5}>5</MenuItem>
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={25}>25</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                          </Select>
                          <Typography variant="body2">Go to</Typography>

                          <TextField
                            type="number"
                            variant="outlined"
                            size="small"
                            value={page.page + 1}
                            onChange={handleDirectPageChange}
                            inputProps={{
                              min: 1,
                              max: Math.ceil(totalRecords / page.limit),
                              style: { padding: '4px 8px', width: '40px' }
                            }}
                          />
                        </Stack>

                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Pagination
                            count={Math.ceil(totalRecords / page.limit)}
                            page={page.page + 1}
                            onChange={handlePageChange}
                            color="primary"
                            size="small"
                            showFirstButton
                            showLastButton
                          />
                        </Stack>
                      </Stack>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Stack>
          </Box>
        </ScrollX>
      </MainCard>
    );
  }
);

export default OrdersTableV2;
